import {
  ArrowCircleUp,
  Broadcast,
  ChalkboardTeacher,
  CreditCard,
  Crosshair,
  Gear,
  GraduationCap,
  Graph,
  // Headphones,
  Headset,
  House,
  Joystick,
  Question,
  SignOut,
  UserCircle,
  Wrench,
  ListChecks,
} from '@phosphor-icons/react';
import OraLogo from '../OraLogo';
import { TabTypes } from '../../../../types/Global';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { hapticsImpactLight } from '../../../../utils/haptics';
import { useAppSelector } from '@/store/hooks';
import { Capacitor } from '@capacitor/core';
import { selectIsImpersonating } from '../../../../slices/auth/authSlice';
import { Chart } from '../../../../assets/svgs/Chart';
import { ChartActive } from '../../../../assets/svgs/ChartActive';
import { Videos } from '../../../../assets/svgs/Videos';
import { VideosActive } from '../../../../assets/svgs/VideosActive';
import { signOut, stopImpersonating } from '../../../../services/auth';
import DailyLimits from '../../Upgrade/DailyLimits';
import { useAccess } from '../../../../hooks/useAccess';
import { selectIsTrialEligible } from '../../../../slices/subscriptions/subscriptionsSlice';
import { getUpgradeCtaLabel } from '../../../../utils/upgradeCopy';
import { requestAmbassador } from '../../../../services/users';
import { enqueueSnackbar } from 'notistack';
import List from '../../../../assets/svgs/List';
import ListActive from '../../../../assets/svgs/ListActive';
import { TreeView, TreeViewActive } from '../../../../assets/svgs/TreeView';
import _ from 'lodash';
import defaultProfilePicture from '../../../../assets/images/blob-avatar.png';
import { UserRole } from '../../../../types/User';
import { isUserInUS } from '../../../../utils/isUserInUS';
import { Stack, StackActive } from '../../../../assets/svgs/Stack';
import ReferralModal from '../ReferralModal';
import { Gift } from '../../../../assets/svgs/Gift';
import FreeOraModal from './FreeOraModal';
import {
  buildAuthPath,
  getCurrentPathWithSearch,
} from '../../../../utils/authRedirect';
import { showIntercom } from '../../../../utils/intercom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ClipboardText,
  ClipboardTextActive,
} from '@/assets/svgs/ClipboardText';
import { Dashboard, DashboardActive } from '@/assets/svgs/Dashboard';
import { AI } from '../../../../assets/svgs/AI';
import { AIActive } from '../../../../assets/svgs/AIActive';
import {
  isTipDismissed,
  markTipSeen,
} from '../../../../helpers/messageStateGating';
import { BookOpen, BookOpenActive } from '@/assets/svgs/BookOpen';
import { getActiveWebinarLiveVisit } from '../../../../services/webinarLive';
import { examsSelectors } from '../../../../slices/exams/examsSlice';
import { isNclexExam, isVideosOffForExam } from '../../../../utils/examUtils';
import {
  MCGRAW_HILL_ROOT,
  useIsMcGrawHill,
} from '../../../../mcgrawHill/mcgrawHill'; // mcgraw-hill
import { McGrawHillTextbooksNavItem } from '../../../../mcgrawHill/McGrawHillNavItems'; // mcgraw-hill
import { DutCoursesNavItem } from '../../../../mcgrawHill/DutCoursesNavItem'; // mcgraw-hill
import { useIsDutCoursesNavActive } from '../../../../mcgrawHill/nursingCourses'; // mcgraw-hill
import {
  useCapability,
  useIsCourseStudent,
} from '../../../../instances/useInstance';
import { useKnowledgeMapEnabled } from '../../../../utils/knowledgeMapFlag';
import { isWebGL2Supported } from '../../../../utils/webglDetection';
import {
  requestKnowledgeMapClose,
  useIsKnowledgeMapOpen,
} from '../../../../os/knowledgeMapOpenStore';
import { OS_DESKTOP_MEDIA_QUERY } from '../../../../os/osRouteMeta';
import { useMatchMedia } from '../../../../hooks/useMatchMedia';

interface NavBarProps {
  shouldAutoOpenReferFriendModal?: boolean;
  onAutoOpenReferFriendModal?: () => void;
  mode?: 'study' | 'faculty';
}

const NavBar: React.FC<NavBarProps> = ({
  shouldAutoOpenReferFriendModal = false,
  onAutoOpenReferFriendModal,
  mode = 'study',
}) => {
  const currentUser = useAppSelector((state) => state.user.profile);

  // const isAudioOverviewsAvailable = useAppSelector(
  //   (state) => state.audioOverviews?.config.status === 'ready'
  // );

  const hasDismissedReferFriendHighlight = useAppSelector((state) =>
    isTipDismissed(state, state.user.profile, 'highlight-refer-friend')
  );

  const subscription = useAppSelector(
    (state) => state.subscription.subscription
  );

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isImpersonating = useAppSelector(selectIsImpersonating);
  const school = useAppSelector((state) => state.school.school);
  const { isGated } = useAccess();
  const isTrialEligible = useAppSelector(selectIsTrialEligible);
  const [activeTab, setActiveTab] = useState<TabTypes>(TabTypes.Home);
  const [freeOraModalIsOpen, setFreeOraModalIsOpen] = useState(false);
  const [referFriendModalIsOpen, setReferFriendModalIsOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [freeOraMenuOpen, setFreeOraMenuOpen] = useState(false);
  const [highlightReferAFriend, setHighlightReferAFriend] = useState(false);
  // Set when a nav option is clicked so the rail collapses to icons right
  // away instead of staying expanded under the pointer; cleared on mouseleave
  // so the next hover expands normally.
  const [isHoverExpandSuppressed, setIsHoverExpandSuppressed] = useState(false);

  // A click that opened an in-rail submenu shouldn't suppress hover: the
  // menu-open class holds the rail expanded, and when the menu closes with
  // the pointer still inside, hover must keep it open instead of snapping
  // to icons mid-hover.
  useEffect(() => {
    if (accountMenuOpen || freeOraMenuOpen) {
      setIsHoverExpandSuppressed(false);
    }
  }, [accountMenuOpen, freeOraMenuOpen]);

  const [webinarLiveVisit, setWebinarLiveVisit] = useState<{
    cohort: string;
  } | null>(null);

  const isLikelyUSStudent = (() => {
    const country = school?.country?.trim().toUpperCase();

    if (country) {
      return (
        country === 'US' ||
        country === 'USA' ||
        country === 'UNITED STATES' ||
        country === 'UNITED STATES OF AMERICA'
      );
    }

    if (school?.state) return true;

    return isUserInUS(currentUser?.timezone);
  })();

  const freeOraButtonRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const showAmbassadorModal =
    !(Capacitor.getPlatform() === 'android') &&
    isGated() &&
    !currentUser?.ambassador &&
    // A school or organization that hosts a webinar is already covered by the
    // webinar launch motion, so it doesn't need an ambassador recruited too.
    !currentUser?.hasWebinarHost &&
    school?.verified &&
    currentUser?.role !== UserRole.admin &&
    isLikelyUSStudent;

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isGamesRoute = pathname.startsWith('/games');
  const isFacultyMode = mode === 'faculty';
  const isFacultyRoute = pathname.startsWith('/faculty');
  const isGuestGamesRoute = !isAuthenticated && isGamesRoute;

  const isPreOnboardingGamesRoute =
    isAuthenticated && currentUser?.isOnboarded === false && isGamesRoute;

  const shouldRenderGamesNavOption =
    currentUser?.isOnboarded || isPreOnboardingGamesRoute;

  // Single-exam rule: an NCLEX primary has no videos at launch and the games
  // are Step-only, so their entries are hidden (and the routes redirect home).
  const primaryExam = useAppSelector(examsSelectors.selectPrimaryExam);
  const showsVideosNav = !isVideosOffForExam(primaryExam);
  // mcgraw-hill: the catalog replaces Ora's Library.
  const isMcGrawHill = useIsMcGrawHill();
  const logoPath = isMcGrawHill ? MCGRAW_HILL_ROOT : '/';
  // A partner-course instance (Courses V1) swaps in the course home items.
  const isCourseStudent = useIsCourseStudent();
  // Partner commerce hides billing and referrals; Games has an opt-in.
  const isPartnerCommerce = useCapability('commerce') === 'partner';
  const hasStudyLibrary = useCapability('studyLibrary') !== 'none';
  const hasGames = useCapability('games') === 'on' || !isPartnerCommerce;
  // The knowledge map (WebGL, the OS desktop only) has its own tab for a
  // course student, at desktop width; it opens over the desktop at `/`.
  const isKnowledgeMapEnabled = useKnowledgeMapEnabled();
  const isOsDesktopViewport = useMatchMedia(OS_DESKTOP_MEDIA_QUERY);
  const isKnowledgeMapOpen = useIsKnowledgeMapOpen();

  const showsKnowledgeMapNav =
    isCourseStudent &&
    isKnowledgeMapEnabled &&
    isOsDesktopViewport &&
    isWebGL2Supported();

  const showsGamesNav = !isNclexExam(primaryExam) && hasGames;

  const hasInstitutionBasedAccess = Boolean(
    subscription?.institutionalAccess || subscription?.institutionalAdminAccess
  );

  const hasFacultyDashboardAccess = Boolean(
    subscription?.institutionalAdminAccess
  );

  const showReferFriendOption =
    !Capacitor.isNativePlatform() &&
    !hasInstitutionBasedAccess &&
    !isPartnerCommerce;

  useEffect(() => {
    // The desktop at / is the AI chat; the Home tab is the Dashboard.
    // A course student's map is a tab of its own while it is open there.
    if (pathname === '/' && isCourseStudent && isKnowledgeMapOpen) {
      setActiveTab(TabTypes.KnowledgeMap);
    } else if (pathname === '/') {
      setActiveTab(TabTypes.Chat);
    } else if (pathname === '/dashboard') {
      setActiveTab(TabTypes.Home);
    } else if (pathname === '/modules') {
      setActiveTab(TabTypes.Modules);
    } else if (pathname.startsWith('/study/webinar')) {
      setActiveTab(TabTypes.Webinar);
    } else if (pathname === '/performance') {
      setActiveTab(TabTypes.Stats);
    } else if (pathname === '/library') {
      setActiveTab(TabTypes.Library);
    } else if (pathname.includes('/topics')) {
      setActiveTab(TabTypes.Topics);
    } else if (pathname === '/videos' || pathname.includes('/videos/')) {
      setActiveTab(TabTypes.Videos);
    } else if (pathname === '/account' || pathname.startsWith('/help')) {
      setActiveTab(TabTypes.Account);
    } else if (pathname.includes('/games')) {
      setActiveTab(TabTypes.Games);
    } else if (pathname.startsWith('/self-assessments')) {
      setActiveTab(TabTypes.SelfAssessments);
    } else if (pathname === '/audio-overviews') {
      setActiveTab(TabTypes.AudioOverviews);
    } else if (pathname.includes('/qbank')) {
      setActiveTab(TabTypes.QBank);
    } else if (
      pathname === '/flashcards' ||
      pathname.startsWith('/flashcards/') ||
      pathname.includes('/flashcard-decks') ||
      pathname.includes('/upload-deck')
    ) {
      setActiveTab(TabTypes.FlashcardDecks);
    } else {
      setActiveTab(TabTypes.null);
    }
  }, [pathname, isCourseStudent, isKnowledgeMapOpen]);

  // Reading the visit is not free of side effects — an expired entry is
  // cleared from localStorage on read — so it must not happen in the render
  // body. Re-read on navigation, the same trigger the active tab uses.
  useEffect(() => {
    setWebinarLiveVisit(getActiveWebinarLiveVisit());
  }, [pathname]);

  useEffect(() => {
    if (Capacitor.isNativePlatform() || showAmbassadorModal) return;
    if (hasInstitutionBasedAccess) {
      setHighlightReferAFriend(false);
      return;
    }
    if (!currentUser || !currentUser?.onboardingTips) return;
    setHighlightReferAFriend(!hasDismissedReferFriendHighlight);
  }, [
    currentUser?.onboardingTips,
    hasDismissedReferFriendHighlight,
    hasInstitutionBasedAccess,
    showAmbassadorModal,
  ]);

  // Auto-open at most once per mount. The callback records the
  // 'promote-referral' tip, and that write can fail (older backend, network);
  // without this guard the modal would reopen every time it is closed.
  const hasAutoOpenedReferFriendModalRef = useRef(false);

  useEffect(() => {
    if (!shouldAutoOpenReferFriendModal) return;
    if (!currentUser?.isOnboarded) return;
    if (!showReferFriendOption) return;
    if (referFriendModalIsOpen) return;
    if (hasAutoOpenedReferFriendModalRef.current) return;

    hasAutoOpenedReferFriendModalRef.current = true;
    setReferFriendModalIsOpen(true);
    onAutoOpenReferFriendModal?.();
  }, [
    currentUser?.isOnboarded,
    onAutoOpenReferFriendModal,
    referFriendModalIsOpen,
    shouldAutoOpenReferFriendModal,
    showReferFriendOption,
  ]);

  const handleStopImpersonating = async () => {
    const stopped = await stopImpersonating();
    if (stopped) {
      navigate('/admin');
    } else {
      enqueueSnackbar('Could not stop impersonating. Please try again.', {
        variant: 'error',
      });
    }
  };

  const shouldShowCombinedFreeOraMenu =
    showAmbassadorModal && showReferFriendOption;

  const freeOraMenuContentStyle = {
    width: freeOraButtonRef.current?.offsetWidth,
    minWidth: 'auto',
  };

  const renderAccountButtonContent = () => (
    <>
      <img
        className="app-bar-user-avatar"
        src={
          !_.isNil(currentUser?.profilePictureUrl) &&
          !_.isEmpty(currentUser?.profilePictureUrl)
            ? currentUser?.profilePictureUrl
            : defaultProfilePicture
        }
        alt={currentUser?.name || 'Profile Picture'}
      />
      <span>Account</span>
    </>
  );

  const recordReferAFriendOnboardingTip = async () => {
    if (!currentUser) return;
    setHighlightReferAFriend(false);

    await markTipSeen('highlight-refer-friend');
  };

  // mcgraw-hill (ORA-1261): Nursing's Courses takes the highlight from
  // Dashboard.
  const isDutCoursesActive = useIsDutCoursesNavActive(pathname);

  const isDashboardNavActive =
    activeTab === TabTypes.Home && !isDutCoursesActive;

  const renderTopOptions = (iconsOnly: boolean) => {
    if (isFacultyMode) {
      return (
        <div className="navbar_options">
          {!iconsOnly && renderSkipNavigation()}
          <button
            className={`navbar_options_option ${isFacultyRoute ? 'active' : ''}`}
            onClick={() => {
              hapticsImpactLight();
              navigate('/faculty');
            }}
            aria-label="Navigate to dashboard"
            tabIndex={iconsOnly ? -1 : 0}
          >
            <House />
            <span>Dashboard</span>
          </button>
        </div>
      );
    }

    return (
      <div className="navbar_options">
        {!iconsOnly && renderSkipNavigation()}
        {/* The AI desktop at / — the companion's full page — leads the rail.
            A guest on the public games has no desktop to go to. */}
        {!isGuestGamesRoute && (
          <button
            className={`navbar_options_option ${
              activeTab === TabTypes.Chat ? 'active' : ''
            }`}
            onClick={() => {
              hapticsImpactLight();
              // A course student's Home with the map open: the route is
              // already /, so the map is asked to close and the start
              // screen returns.
              if (isCourseStudent) requestKnowledgeMapClose();
              navigate('/');
            }}
            aria-label="Navigate to AI"
            tabIndex={iconsOnly ? -1 : 0}
          >
            {/* A course student's home is the course: the Videos tab's play
                mark, not the companion's sparkle. */}
            {isCourseStudent ? (
              activeTab === TabTypes.Chat ? (
                <VideosActive />
              ) : (
                <Videos />
              )
            ) : activeTab === TabTypes.Chat ? (
              <AIActive />
            ) : (
              <AI />
            )}
            <span>{isCourseStudent ? 'Home' : 'Ora AI'}</span>
          </button>
        )}
        {/* A course student's desktop is their home; there is no Dashboard. */}
        {!isCourseStudent && (
          <button
            className={`navbar_options_option ${
              (
                isGuestGamesRoute
                  ? activeTab === TabTypes.Games
                  : isDashboardNavActive
              )
                ? 'active'
                : ''
            }`}
            onClick={() => {
              hapticsImpactLight();
              navigate(isGuestGamesRoute ? '/games' : '/dashboard');
            }}
            aria-label={
              isGuestGamesRoute ? 'Navigate to Games' : 'Navigate to Dashboard'
            }
            tabIndex={iconsOnly ? -1 : 0}
          >
            {isGuestGamesRoute ? (
              activeTab === TabTypes.Games ? (
                <Joystick weight="fill" />
              ) : (
                <Joystick />
              )
            ) : isDashboardNavActive ? (
              <DashboardActive />
            ) : (
              <Dashboard />
            )}
            <span>{isGuestGamesRoute ? 'Games' : 'Dashboard'}</span>
          </button>
        )}
        <DutCoursesNavItem iconsOnly={iconsOnly} />
        {isCourseStudent && currentUser?.isOnboarded && (
          <button
            className={`navbar_options_option ${activeTab === TabTypes.Modules ? 'active' : ''}`}
            onClick={() => {
              hapticsImpactLight();
              navigate('/modules');
            }}
            aria-label="Navigate to Modules"
            tabIndex={iconsOnly ? -1 : 0}
          >
            <ListChecks
              size={24}
              weight={activeTab === TabTypes.Modules ? 'fill' : 'regular'}
              aria-hidden="true"
            />
            <span>Modules</span>
          </button>
        )}
        {showsKnowledgeMapNav && currentUser?.isOnboarded && (
          <button
            className={`navbar_options_option ${activeTab === TabTypes.KnowledgeMap ? 'active' : ''}`}
            onClick={() => {
              hapticsImpactLight();
              navigate('/?map');
            }}
            aria-label="Navigate to Knowledge Map"
            tabIndex={iconsOnly ? -1 : 0}
          >
            <Graph
              size={24}
              weight={activeTab === TabTypes.KnowledgeMap ? 'fill' : 'regular'}
              aria-hidden="true"
            />
            <span>Knowledge Map</span>
          </button>
        )}
        {shouldRenderGamesNavOption ? (
          currentUser?.isOnboarded ? (
            <>
              <button
                className={`navbar_options_option ${activeTab === TabTypes.Topics ? 'active' : ''}`}
                onClick={() => {
                  hapticsImpactLight();
                  navigate('/topics');
                }}
                aria-label="Navigate to Topics"
                tabIndex={iconsOnly ? -1 : 0}
              >
                {activeTab === TabTypes.Topics ? (
                  <TreeViewActive />
                ) : (
                  <TreeView />
                )}
                <span>Topics</span>
              </button>
              <button
                className={`navbar_options_option ${activeTab === TabTypes.Stats ? 'active' : ''}`}
                onClick={() => {
                  hapticsImpactLight();
                  navigate('/performance');
                }}
                aria-label="Navigate to Performance"
                tabIndex={iconsOnly ? -1 : 0}
              >
                {activeTab === TabTypes.Stats ? <ChartActive /> : <Chart />}
                <span>Performance</span>
              </button>
              {/* An instance without Ora's study library has no free-study
                  tabs (Videos, QBank, Flashcards, Library, Self-Assessments). */}
              {hasStudyLibrary && (
                <>
                  <div className="navbar_options_divider"></div>
                  {showsVideosNav && (
                    <button
                      className={`navbar_options_option ${activeTab === TabTypes.Videos ? 'active' : ''}`}
                      onClick={() => {
                        hapticsImpactLight();
                        navigate('/videos');
                      }}
                      aria-label="Navigate to Videos"
                      tabIndex={iconsOnly ? -1 : 0}
                    >
                      {activeTab === TabTypes.Videos ? (
                        <VideosActive />
                      ) : (
                        <Videos />
                      )}
                      <span>Videos</span>
                    </button>
                  )}
                  <button
                    className={`navbar_options_option ${activeTab === TabTypes.QBank ? 'active' : ''}`}
                    onClick={() => {
                      hapticsImpactLight();
                      navigate('/qbank-start');
                    }}
                    aria-label="Navigate to QBank"
                    tabIndex={iconsOnly ? -1 : 0}
                  >
                    {activeTab === TabTypes.QBank ? <ListActive /> : <List />}
                    <span>QBank</span>
                  </button>
                  <button
                    className={`navbar_options_option ${activeTab === TabTypes.FlashcardDecks ? 'active' : ''}`}
                    onClick={() => {
                      hapticsImpactLight();
                      navigate('/flashcards');
                    }}
                    aria-label="Navigate to Flashcards"
                    tabIndex={iconsOnly ? -1 : 0}
                  >
                    {activeTab === TabTypes.FlashcardDecks ? (
                      <StackActive />
                    ) : (
                      <Stack />
                    )}
                    <span>Flashcards</span>
                  </button>
                  {/* mcgraw-hill */}
                  {!isMcGrawHill && (
                    <button
                      className={`navbar_options_option ${activeTab === TabTypes.Library ? 'active' : ''}`}
                      onClick={() => {
                        hapticsImpactLight();
                        navigate('/library');
                      }}
                      aria-label="Navigate to Library"
                      tabIndex={iconsOnly ? -1 : 0}
                    >
                      {activeTab === TabTypes.Library ? (
                        <BookOpenActive />
                      ) : (
                        <BookOpen />
                      )}
                      <span>Library</span>
                    </button>
                  )}
                  <button
                    className={`navbar_options_option ${activeTab === TabTypes.SelfAssessments ? 'active' : ''}`}
                    onClick={() => {
                      hapticsImpactLight();
                      navigate('/self-assessments');
                    }}
                    aria-label="Navigate to Self-Assessments"
                    tabIndex={iconsOnly ? -1 : 0}
                  >
                    {activeTab === TabTypes.SelfAssessments ? (
                      <ClipboardTextActive />
                    ) : (
                      <ClipboardText />
                    )}
                    <span>Self-Assessments</span>
                  </button>
                  {isMcGrawHill && (
                    <McGrawHillTextbooksNavItem iconsOnly={iconsOnly} />
                  )}
                </>
              )}
              {/* Only once /config confirms the feature for this account
                  (the app-wide watcher loads it). */}
              {/* {isAudioOverviewsAvailable && (
                <button
                  className={`navbar_options_option ${activeTab === TabTypes.AudioOverviews ? 'active' : ''}`}
                  onClick={() => {
                    hapticsImpactLight();
                    navigate('/audio-overviews');
                  }}
                  aria-label="Navigate to Audio Overviews"
                  tabIndex={iconsOnly ? -1 : 0}
                >
                  <Headphones
                    weight={
                      activeTab === TabTypes.AudioOverviews ? 'fill' : 'regular'
                    }
                  />
                  <span>Audio Overviews</span>
                </button>
              )} */}
              {showsGamesNav && (
                <>
                  <div className="navbar_options_divider"></div>
                  <button
                    className={`navbar_options_option navbar_options_option--games ${activeTab === TabTypes.Games ? 'active' : ''}`}
                    onClick={() => {
                      hapticsImpactLight();
                      navigate('/games');
                    }}
                    aria-label="Navigate to Games"
                    tabIndex={iconsOnly ? -1 : 0}
                  >
                    {activeTab === TabTypes.Games ? (
                      <Joystick weight="fill" />
                    ) : (
                      <Joystick />
                    )}
                    <span>Games</span>
                  </button>
                </>
              )}
            </>
          ) : (
            <button
              className={`navbar_options_option navbar_options_option--games ${activeTab === TabTypes.Games ? 'active' : ''}`}
              onClick={() => {
                hapticsImpactLight();
                navigate('/games');
              }}
              aria-label="Navigate to Games"
              tabIndex={iconsOnly ? -1 : 0}
            >
              {activeTab === TabTypes.Games ? (
                <Joystick weight="fill" />
              ) : (
                <Joystick />
              )}
              <span>Games</span>
            </button>
          )
        ) : null}
        {webinarLiveVisit && (
          <button
            className={`navbar_options_option ${activeTab === TabTypes.Webinar ? 'active' : ''}`}
            onClick={() => {
              hapticsImpactLight();

              navigate(
                `/study/webinar/${encodeURIComponent(webinarLiveVisit.cohort)}`
              );
            }}
            aria-label="Navigate to live event"
            tabIndex={iconsOnly ? -1 : 0}
          >
            <Broadcast
              className="webinar-live-nav-icon"
              weight={activeTab === TabTypes.Webinar ? 'fill' : 'regular'}
            />
            <span>Live Event</span>
          </button>
        )}
      </div>
    );
  };

  // Shared account avatar + popup used by both the study app and faculty
  // navbars. The logout destination differs by context (faculty signs back in
  // through the faculty auth route), so it is parameterized.
  const renderAccountMenu = (iconsOnly: boolean, logoutRedirect: string) => {
    if (!isAuthenticated) return null;

    if (iconsOnly) {
      return (
        <button
          className={`navbar_options_option has-avatar ${activeTab === TabTypes.Account ? 'active' : ''}`}
          aria-controls={accountMenuOpen ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={accountMenuOpen ? 'true' : undefined}
          onClick={() => {
            hapticsImpactLight();
            setAccountMenuOpen(true);
          }}
          tabIndex={-1}
          aria-label="Access account settings"
        >
          {renderAccountButtonContent()}
        </button>
      );
    }

    return (
      <DropdownMenu open={accountMenuOpen} onOpenChange={setAccountMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            className={`navbar_options_option has-avatar ${activeTab === TabTypes.Account ? 'active' : ''}`}
            id="account-menu-button"
            aria-controls={accountMenuOpen ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={accountMenuOpen ? 'true' : undefined}
            onClick={() => {
              hapticsImpactLight();
            }}
            tabIndex={0}
            aria-label="Access account settings"
          >
            {renderAccountButtonContent()}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          id="account-menu"
          aria-labelledby="account-menu-button"
          side="top"
          align="start"
          sideOffset={4}
          className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-0"
        >
          {currentUser?.isOnboarded && (
            <>
              <DropdownMenuItem
                onSelect={() => {
                  setAccountMenuOpen(false);
                  navigate('/account?sub=profile');
                  hapticsImpactLight();
                }}
                aria-label="Access profile settings"
              >
                <UserCircle />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setAccountMenuOpen(false);
                  navigate('/account?sub=settings');
                  hapticsImpactLight();
                }}
                aria-label="Access settings"
              >
                <Gear />
                <span>Settings</span>
              </DropdownMenuItem>
              {/* A partner that sells access itself: no Billing. */}
              {!isPartnerCommerce && (
                <DropdownMenuItem
                  onSelect={() => {
                    setAccountMenuOpen(false);
                    navigate('/account?sub=billing');
                    hapticsImpactLight();
                  }}
                  aria-label="Access billing"
                >
                  <CreditCard />
                  <span>Billing</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => {
                  setAccountMenuOpen(false);
                  void showIntercom();
                  hapticsImpactLight();
                }}
                aria-label="Contact support"
              >
                <Headset />
                <span>Support</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setAccountMenuOpen(false);
                  navigate('/help');
                  hapticsImpactLight();
                }}
                aria-label="Open help docs"
              >
                <Question />
                <span>Help Docs</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="my-1 bg-white/10" />
            </>
          )}
          <DropdownMenuItem
            className={!currentUser?.isOnboarded ? 'mt-1' : undefined}
            onSelect={() => {
              setAccountMenuOpen(false);
              hapticsImpactLight();
              void signOut().then(() => navigate(logoutRedirect));
            }}
            aria-label="Log out"
          >
            <SignOut />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderBottomOptions = (iconsOnly: boolean) => {
    const authRedirect = buildAuthPath(getCurrentPathWithSearch());

    if (isFacultyMode) {
      return (
        <div className="navbar_options">
          <button
            className="navbar_options_option"
            onClick={() => {
              hapticsImpactLight();
              navigate('/');
            }}
            aria-label="Go to main app"
            tabIndex={iconsOnly ? -1 : 0}
          >
            <GraduationCap />
            <span>Main App</span>
          </button>
          {isAuthenticated ? (
            renderAccountMenu(iconsOnly, '/faculty/auth')
          ) : (
            <button
              className="navbar_options_option"
              onClick={() => {
                hapticsImpactLight();
                navigate(authRedirect);
              }}
              tabIndex={iconsOnly ? -1 : 0}
              aria-label="Sign in"
            >
              <UserCircle />
              <span>Sign In</span>
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="navbar_options">
        {!isAuthenticated ? (
          <button
            className="navbar_options_option"
            onClick={() => {
              hapticsImpactLight();
              navigate(authRedirect);
            }}
            tabIndex={iconsOnly ? -1 : 0}
            aria-label="Sign in"
          >
            <UserCircle />
            <span>Sign In</span>
          </button>
        ) : null}
        {currentUser?.isOnboarded && (
          <>
            {!(Capacitor.getPlatform() === 'android') && isGated() && (
              <button
                className={`navbar_options_option navbar_options_option--subscribe`}
                onClick={() => {
                  hapticsImpactLight();
                  navigate('/pricing');
                }}
                aria-label={getUpgradeCtaLabel(isTrialEligible)}
                tabIndex={iconsOnly ? -1 : 0}
              >
                {!iconsOnly && <DailyLimits />}
                <div className="text-wrapper">
                  <ArrowCircleUp />
                  <span>{getUpgradeCtaLabel(isTrialEligible)}</span>
                </div>
              </button>
            )}
            {isImpersonating && (
              <button
                className="navbar_options_option"
                onClick={handleStopImpersonating}
                style={{ backgroundColor: 'red' }}
                aria-label="Stop impersonating user"
                tabIndex={iconsOnly ? -1 : 0}
              >
                <Crosshair />
                <span>Stop Impersonating</span>
              </button>
            )}
            {currentUser?.role === 'admin' && !currentUser?.demoMode && (
              <button
                className={`navbar_options_option`}
                onClick={() => {
                  hapticsImpactLight();
                  navigate('/admin');
                }}
                aria-label="Access admin dashboard"
                tabIndex={iconsOnly ? -1 : 0}
              >
                <Wrench />
                <span>Admin Dashboard</span>
              </button>
            )}
            {hasFacultyDashboardAccess && (
              <button
                className={`navbar_options_option`}
                onClick={() => {
                  hapticsImpactLight();
                  navigate('/faculty');
                }}
                aria-label="Access admin dashboard"
                tabIndex={iconsOnly ? -1 : 0}
              >
                <ChalkboardTeacher />
                <span>Faculty Dashboard</span>
              </button>
            )}
            {(showAmbassadorModal || showReferFriendOption) && (
              <>
                {shouldShowCombinedFreeOraMenu ? (
                  iconsOnly ? (
                    <button
                      className={`navbar_options_option ${highlightReferAFriend ? 'highlight button-shimmer' : ''}`}
                      onClick={() => {
                        hapticsImpactLight();
                        setAccountMenuOpen(false);
                        setFreeOraMenuOpen(true);
                      }}
                      aria-controls={
                        freeOraMenuOpen ? 'free-ora-menu' : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={freeOraMenuOpen ? 'true' : undefined}
                      aria-label="Get free Ora"
                      tabIndex={-1}
                    >
                      <Gift />
                      <span>Get Ora for Free</span>
                    </button>
                  ) : (
                    <DropdownMenu
                      open={freeOraMenuOpen}
                      onOpenChange={setFreeOraMenuOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <button
                          ref={freeOraButtonRef}
                          className={`navbar_options_option ${highlightReferAFriend ? 'highlight button-shimmer' : ''}`}
                          onClick={() => {
                            hapticsImpactLight();
                            setAccountMenuOpen(false);
                          }}
                          aria-label="Get free Ora"
                          tabIndex={0}
                        >
                          <Gift />
                          <span>Get Ora for Free</span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        id="free-ora-menu"
                        side="top"
                        align="start"
                        sideOffset={4}
                        style={freeOraMenuContentStyle}
                      >
                        <DropdownMenuItem
                          onSelect={() => {
                            setFreeOraMenuOpen(false);
                            setFreeOraModalIsOpen(true);
                            hapticsImpactLight();
                          }}
                          aria-label="Be an Ambassador"
                        >
                          <span>Be an Ambassador</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            setFreeOraMenuOpen(false);
                            setReferFriendModalIsOpen(true);
                            hapticsImpactLight();
                            void recordReferAFriendOnboardingTip();
                          }}
                          aria-label="Refer a Friend"
                        >
                          <span>Refer a Friend</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )
                ) : (
                  <button
                    className={`navbar_options_option ${highlightReferAFriend ? 'highlight button-shimmer' : ''}`}
                    onClick={() => {
                      hapticsImpactLight();
                      setAccountMenuOpen(false);

                      if (showAmbassadorModal) {
                        setFreeOraModalIsOpen(true);
                        return;
                      }

                      setReferFriendModalIsOpen(true);
                      void recordReferAFriendOnboardingTip();
                    }}
                    aria-label={
                      showAmbassadorModal ? 'Get free Ora' : 'Refer a Friend'
                    }
                    tabIndex={iconsOnly ? -1 : 0}
                  >
                    <Gift />
                    <span>
                      {showAmbassadorModal
                        ? 'Get Ora for Free'
                        : 'Refer a Friend'}
                    </span>
                  </button>
                )}
              </>
            )}
          </>
        )}
        {renderAccountMenu(iconsOnly, '/auth')}
      </div>
    );
  };

  const renderSkipNavigation = () => {
    return (
      <button
        className="sr-only"
        onClick={() => {
          const navEl = navRef.current;
          if (!navEl) return;

          const selector = [
            'a[href]',
            'area[href]',
            'button:not([disabled])',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[contenteditable="true"]',
            '[tabindex]:not([tabindex="-1"])',
          ].join(',');

          const isVisible = (el: HTMLElement) => {
            const style = window.getComputedStyle(el);
            if (style.visibility === 'hidden' || style.display === 'none')
              return false;
            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
          };

          const candidates = Array.from(
            document.querySelectorAll<HTMLElement>(selector)
          );

          const target = candidates.find(
            (el) => !navEl.contains(el) && isVisible(el) && el.tabIndex !== -1
          );

          if (target) target.focus();
        }}
        aria-label="Skip to content"
        tabIndex={0}
      >
        Skip to content
      </button>
    );
  };

  const handleRequestToJoin = async () => {
    const success = await requestAmbassador(school?.name);
    if (success) {
      setFreeOraModalIsOpen(false);

      enqueueSnackbar(
        'Thank you for your interest! We will get back to you soon.',
        {
          autoHideDuration: 5000,
        }
      );
    } else {
      // Optionally show an error message to the user
      console.error('Failed to send ambassador request');
    }
  };

  return (
    <nav
      ref={navRef}
      className={`navbar ${accountMenuOpen || freeOraMenuOpen ? 'account-menu-open' : ''} ${
        isHoverExpandSuppressed ? 'navbar--hover-suppressed' : ''
      }`}
      // Picking a destination tucks the rail back to icons immediately, even
      // though the pointer is still over it; leaving once re-arms hover.
      onClick={(event) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest('.navbar_options_option')) {
          setIsHoverExpandSuppressed(true);
        }
      }}
      onMouseLeave={() => setIsHoverExpandSuppressed(false)}
    >
      <div className="navbar_icons">
        <div className="navbar_icons_top">
          <div className="app-icon">
            {/* mcgraw-hill: the logo goes to the catalog. */}
            <OraLogo type={'link'} url={logoPath} showCoBrand={false} />
          </div>
          {renderTopOptions(true)}
        </div>
        <div className="navbar_icons_bottom">{renderBottomOptions(true)}</div>
      </div>
      <div className="navbar_full">
        <div className="navbar_full_top">
          <div className="app-icon">
            <OraLogo
              type={'link'}
              url={logoPath}
              aria-label="Ora"
              variant="wordmark"
            />
          </div>
          {renderTopOptions(false)}
        </div>
        <div className="navbar_full_bottom">{renderBottomOptions(false)}</div>
      </div>
      <FreeOraModal
        open={freeOraModalIsOpen}
        onClose={() => setFreeOraModalIsOpen(false)}
        schoolName={school?.name}
        onRequestToJoin={handleRequestToJoin}
        canRequestToJoin={Boolean(school && school.verified)}
      />
      {currentUser && (
        <ReferralModal
          referFriendModalIsOpen={referFriendModalIsOpen}
          setReferFriendModalIsOpen={setReferFriendModalIsOpen}
        />
      )}
    </nav>
  );
};

export default NavBar;
